/**
 * Regular expression for validating email addresses.
 * Matches standard email format: local-part@domain.tld
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates an email address format.
 * @param email - The email address to validate
 * @returns True if email is empty (optional field) or valid format, false otherwise
 */
export const validateEmail = (email: string): boolean => {
  if (!email) return true;
  return EMAIL_REGEX.test(email);
};

/**
 * Special characters accepted by the backend password-strength check.
 * MUST stay in sync with PasswordService.IsPasswordStrong on the server
 * (DRIS.Infrastructure/Services/PasswordService.cs) — the backend silently
 * rejects weak passwords with a neutral response, so any frontend/backend
 * drift would let a "valid" form submit produce no account and no email.
 */
export const PASSWORD_SPECIAL_CHARS = `!@#$%^&*()_+-=[]{};':"\\|,.<>?`;

/** Human-readable summary of the password policy, for helper text. */
export const PASSWORD_REQUIREMENTS_HINT =
  'At least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.';

const PASSWORD_SPECIAL_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/;

/**
 * Validates password strength against the same rules the backend enforces.
 * Returns a specific error message for the first unmet rule, or null if strong.
 */
export const validatePasswordStrength = (password: string): string | null => {
  if (!password || password.length < 8) {
    return 'Password must be at least 8 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain an uppercase letter';
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain a lowercase letter';
  }
  if (!/\d/.test(password)) {
    return 'Password must contain a number';
  }
  if (!PASSWORD_SPECIAL_REGEX.test(password)) {
    return 'Password must contain a special character';
  }
  return null;
};
